import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { RootState, useTypedSelector } from 'src/State/RootReducer'
import useShopLogin from './use-shop-login'
import { useRelogin } from '@e3dc-react/shell/Hooks/use-relogin'
import { useDispatch, useStore } from 'react-redux'
import { authFetchAdd } from '@e3dc-react/shell/State/Auth/AuthActions'
import { useContext } from 'react'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { encodeCriteriaForQuery } from '../utils'
import { updateShopSelectedUser } from 'src/State/Shop/ShopAction'
import { useNavigate } from 'react-router-dom'

/*
 * This hook is used to fetch from shop based on the given details. (URL / PAYLOAD / METHOD ...)
 * It also checks if user get an unauthorized error , relogin and retry back the endpoint.
 */

export type RequestType = AxiosRequestConfig['method']

export type AllowedResponseTypes = 'arraybuffer' | 'blob' | 'document' | 'json' | 'text'

export interface FetchProps {
  path: string
  method?: AxiosRequestConfig['method']
  payload?: FormData | any // eslint-disable-line
  criteria?: unknown
  query?: Record<string, unknown>
  isRetry?: boolean
  token?: string
  endpoint?: string
  option?: Record<string, unknown>
  currentShopAccountToken?: boolean
  usePortalToken?: boolean
  portalTokenOverride?: string
  responseType?: AllowedResponseTypes
  defaultShopAccessKey?: boolean
}
export interface FetchPropsWithResponse extends FetchProps {
  returnResponse: true
}

interface ShopLanguage {
  [key: string]: string
}

const shopCacheHashes = new Map<string, string>()

const languageMap: ShopLanguage = {
  en: 'English',
  de: 'Deutsch',
  /* Add more languages here if needed */
}

export function useShopFetchWithResult(): <T>(props: FetchProps) => Promise<T> {
  return useShopFetch(false)
}

export function useShopFetchWithResponse(): <T>(props: FetchProps) => Promise<AxiosResponse<T>> {
  return useShopFetch(true)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useShopFetch(returnResponse: boolean): any {
  const currentLanguage = localStorage.getItem('i18nextLng')
  const shopLanguage = useTypedSelector(state => state.shop.language)
  const relogin = useRelogin()
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const navigate = useNavigate()
  const { authToken: portalToken } = useContext(AuthContext)

  function getLanguage(): string | undefined {
    if (currentLanguage !== null) {
      const currentLanguageCode = languageMap[currentLanguage] || currentLanguage
      const shopId = shopLanguage.find(shop => shop.name === currentLanguageCode)?.id
      return shopId
    }
  }

  const { shopLogin } = useShopLogin()
  async function _fetch<T>(props: FetchProps): Promise<T>
  async function _fetch<T>(props: FetchPropsWithResponse): Promise<AxiosResponse<T>>

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  async function _fetch<T>(props: FetchProps): Promise<any> {
    const {
      path,
      method = 'GET',
      payload = undefined,
      criteria = undefined,
      query = undefined,
      isRetry = false,
      endpoint = undefined,
      option = undefined,
      currentShopAccountToken = false,
      defaultShopAccessKey = false,
      usePortalToken = false,
      portalTokenOverride = undefined,
      responseType,
    } = props

    const currentShopData = store.getState().shop
    const selectedShopAccessKey = defaultShopAccessKey
      ? process.env.REACT_APP_SHOP_API_ACCESS_KEY
      : currentShopData.shopAccessKey ?? process.env.REACT_APP_SHOP_API_ACCESS_KEY
    const currentShopContextToken = currentShopData.contextToken
    const selectedCustomerShopToken = currentShopData.selectedUserData?.contextToken
    const shopContextToken = selectedCustomerShopToken && !currentShopAccountToken ? selectedCustomerShopToken : currentShopContextToken
    const shopLanguageId = getLanguage()
    const cacheKey = `${selectedShopAccessKey}:${shopContextToken}`
    if (!endpoint && !process.env.REACT_APP_SHOP_API_ENDPOINT) throw new AxiosError('shop endpoint could not be found')

    try {
      if (!isRetry) dispatch(authFetchAdd())
      const url = (endpoint ?? process.env.REACT_APP_SHOP_API_ENDPOINT) + '/' + path

      const options: AxiosRequestConfig = {
        ...option,
        method,
        headers: {},
        params: {
          ...(option?.params as Record<string, unknown> | undefined),
          ...query,
        },
        responseType,
      }

      /* Set header values*/
      if (options.headers) {
        options.headers['sw-access-key'] = selectedShopAccessKey
        options.headers['sw-context-token'] = shopContextToken
        options.headers['sw-language-id'] = shopLanguageId
        const cacheHash = shopCacheHashes.get(cacheKey)

        if (cacheHash) {
          options.headers['sw-cache-hash'] = cacheHash
        }
        if (usePortalToken) {
          options.headers['e3dc-impersonator-portal-token'] = portalTokenOverride ?? portalToken
        }
      }
      /* Set query criteria */
      if (criteria) {
        options.params = {
          ...options.params,
          _criteria: encodeCriteriaForQuery(criteria),
        }
      }

      if (payload) {
        if (payload instanceof FormData) {
          if (options.headers) {
            options.headers['content-type'] = 'multipart/form-data'
          }
          options.data = payload
        } else {
          if (options.headers) {
            options.headers['content-type'] = 'application/json'
          }
          options.data = JSON.stringify(payload)
        }
      }

      let response
      try {
        response = await axios(url, { ...options })
      } catch (err) {
        /* If response is an error, call useShopLogin function and retry again*/
        const unAuthorized = (err as AxiosError)?.response?.status === 401
        const shouldRefreshPortalToken = unAuthorized && selectedCustomerShopToken && usePortalToken && !isRetry

        if (shouldRefreshPortalToken) {
          const newToken = await relogin()
          if (newToken) {
            return await _fetch({ ...props, isRetry: true, portalTokenOverride: newToken })
          }
        }

        if (unAuthorized && selectedCustomerShopToken && !currentShopAccountToken) {
          dispatch(updateShopSelectedUser(null))
          navigate('/shop/homepage')
          throw err
        }

        if (unAuthorized && !isRetry) {
          const newToken = await relogin()
          if (newToken) {
            await shopLogin(newToken)
            return await _fetch({ ...props, isRetry: true, portalTokenOverride: newToken })
          }
        }

        throw err
      }

      const cacheHash = response.headers['sw-cache-hash']
      if (typeof cacheHash === 'string') {
        shopCacheHashes.set(cacheKey, cacheHash)
      }

      if (returnResponse) return response
      return response
    } catch (err) {
      if (!isRetry) {
        console.error('shopFetch > catch', err)
      }
      throw err
    }
  }

  return _fetch
}
